import {apiClient} from 'core/apiClient';
import {OrderStatus} from 'core/dictionaries';
import {ApiFilters, ApiPaging, ApiSortings} from 'types/api';
import {GtinOrderListItem, GtinsModeFilter, ProductStatuses} from 'types/gtins';
import {
    OrderCreateData,
    OrderDetailProductsRaw,
    OrderDetailRaw,
    OrdersGridRawRow,
    OrdersAccessors,
    OrderProductsData,
    FormattedLabelsForm,
} from 'types/orders';
import {FactoriesListRawRow} from 'types/profile';

const ORDERS_URL = '/service/orders';

function mapOrdersGridFilters(filters: ApiFilters) {
    const hasStatusFilter = filters.some((elem) => elem.id === 'status');
    if (!hasStatusFilter) {
        filters.push({
            id: OrdersAccessors.status,
            value: [
                OrderStatus.APPROVED,
                OrderStatus.READY,
                OrderStatus.CLOSED,
                OrderStatus.DECLINED,
            ],
        });
    }
    return filters;
}

export async function fetchOrdersGrid(params: {
    sortings: ApiSortings;
    filters: ApiFilters;
    paging: ApiPaging;
}): Promise<OrdersGridRawRow[]> {
    params.filters = mapOrdersGridFilters(params.filters);
    return (await apiClient.post(ORDERS_URL, params)).data as OrdersGridRawRow[];
    // return (await fakeApi.ordersGrid(ORDERS_URL, params)) as OrdersGridRawRow[];
}

export async function fetchOrdersGridCount(params: {filters: ApiFilters}): Promise<number> {
    const path = `${ORDERS_URL}/count`;
    params.filters = mapOrdersGridFilters(params.filters);
    return (await apiClient.post(path, params)).data as number;
}

export async function fetchOrderCreate(params: OrderCreateData): Promise<any> {
    const data: any = await apiClient.post('/emission/orders', params);
    return {
        data,
    };
}

export async function fetchOrdersDetail(id: string): Promise<OrderDetailRaw> {
    return (await apiClient.post(`${ORDERS_URL}/details`, {orderId: id})).data?.result;
}

export async function fetchGtinInfo(
    products: Array<OrderProductsData>,
): Promise<OrderDetailProductsRaw[]> {
    const gtins = products.map((item) => item.gtin?.toString());
    return (await apiClient.post(`/service/product-cards/by-gtins`, {gtins})).data;
}

export async function fetchOrderProductsGrid(params: {
    orderId: string;
    paging: ApiPaging;
    sortings: ApiSortings;
    filters: ApiFilters;
}): Promise<OrderDetailProductsRaw[]> {
    return (await apiClient.post(`${ORDERS_URL}/details/products`, params))
        .data as OrderDetailProductsRaw[];
}

export async function fetchFormattedLabels(params: Partial<FormattedLabelsForm>) {
    const response = await apiClient.post(`/service/printing/export`, params, {
        responseType: 'blob',
    });
    const blob = response.data;
    const contentDisposition = response.headers['content-disposition'];
    let fileName = `marking_codes_${params.orderId}_${params.gtin}.${(
        params.format ?? 'PDF'
    ).toLowerCase()}`;

    if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match?.[1]) {
            [, fileName] = match;
        }
    }

    saveAs(blob, fileName);
}

export async function filterFactories() {
    return (await apiClient.get<FactoriesListRawRow[]>(`/service/profile/factories`)).data;
}

export async function filterFactoryLines(factoryId: string) {
    return (await apiClient.get(`/service/profile/factories/${factoryId}/factory-lines`)).data;
}

export async function getValidGTINSByManufacturer(ids: number[]) {
    return (
        await apiClient.post<GtinOrderListItem[]>(`/service/product-cards/gtins`, {
            factoryIds: ids,
        })
    ).data;
}

type GetProductsPayload = {
    paging: ApiPaging;
    filters: ApiFilters;
    sortings?: ApiSortings;
    viewMode: 'MY' | 'ALL';
    modeFilter?: GtinsModeFilter;
};
export async function getProducts(params: GetProductsPayload) {
    return (
        await apiClient.post<GtinOrderListItem[]>(`/service/product-cards`, {
            ...params,
        })
    ).data;
}

export async function downloadMarkingCodesByTINS(params: {
    gtin: string;
    orderId: string;
    format: string;
}) {
    const response = await apiClient.post(`/service/printing/export`, params, {
        responseType: 'blob',
    });

    const blob = response.data;
    const contentDisposition = response.headers['content-disposition'];
    let fileName = `marking_codes_${params.orderId}_${params.gtin}.${params.format.toLowerCase()}`;

    if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match?.[1]) {
            [, fileName] = match;
        }
    }

    saveAs(blob, fileName);
}
